import React, { useMemo, useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';

import { styles } from './WorkingHours.styles';
import WorkingHoursHeader from './components/WorkingHoursHeader';
import DayRow from './components/DayRow';
import DayRowSkeleton from './components/DayRowSkeleton';
import ProductionHoursMatrix from './components/ProductionHoursMatrix';
import HolidaysCardContainer from './HolidaysCardContainer';
import MixedScheduleBanner from './components/MixedScheduleBanner';
import MixedSitesBanner from './components/MixedSitesBanner';
import HolidaysModalContainer from './HolidaysModalContainer';
import { WorkingHoursProps } from './WorkingHoursContainer';
import { WorkingDay } from './WorkingHours.types';
import { KonnectAnalytics } from '../../KonnectAnalytics/konnectAnalytics';
import { ReportSite } from 'store/storeTypes';
import { buildProductionHoursConfigurationPayload } from './utils/productionHoursConfigurationMapper';

// Ignores generated slot ids - only the actual schedule (enabled + start/end minutes per
// day) counts for "are there unsaved changes" / "do these sites already have the same hours".
const daysSignature = (list: WorkingDay[]) =>
  JSON.stringify(
    list.map(day => ({
      enabled: day.enabled,
      slots: [...day.slots].map(slot => [slot.startMinutes, slot.endMinutes]).sort((a, b) => a[0] - b[0]),
    }))
  );

const WorkingHoursDesktop = ({
  filterData,
  globalSystemGroup,
  selectedSystemGroup,
  selectedSystemGroupTypes,
  userSystemGroup,
  setSystemGroup,
  setSystemGroupTypes,
  selectedSite,
  selectedSites,
  setSites,
  isMixedSites,
  applyMode,
  startDate,
  days,
  configurationLoadId,
  editor,
  editorValidation,
  onSiteChange,
  onApplyModeChange,
  onStartDateChange,
  onToggleDay,
  onAddSlot,
  onAddSlotAt,
  onUpdateSlot,
  onRemoveSlot,
  onRestoreDay,
  onOpenEditor,
  onCloseEditor,
  onUpdateEditorDraft,
  onSetEditorApplyDays,
  onApplyEditor,
  getProductionHoursConfigurationForSites,
  saveProductionHoursConfigurationForSites,
  getProductionHoursMatrix,
  getProductionHoursHolidays,
  enqueueNotification,
  productionHoursMatrix,
  isLoadingProductionHoursConfiguration,
  isLoadingProductionHoursMatrix,
  isMixedSystemGroupTypes,
  viewingSystemGroupType,
  setViewingSystemGroupType,
  canEdit,
}: WorkingHoursProps) => {
  const readOnly = !canEdit;
  // Union of sites across every selected "Apply to system types" group — a site shared by
  // several groups (e.g. Apollo + DTG) should only be listed once, in the order first seen.
  const sites = useMemo(() => {
    const byId = new Map<string, ReportSite>();
    selectedSystemGroupTypes.forEach(group => {
      (filterData[group]?.sites ?? []).forEach(site => {
        if (site.id && !byId.has(site.id)) byId.set(site.id, site);
      });
    });
    return Array.from(byId.values());
  }, [filterData, selectedSystemGroupTypes]);

  const currentSiteName = sites.find(s => s.id === selectedSite)?.name ?? selectedSite;

  // Real site names for every checked "apply to" site (falls back to just the viewing site
  // when none are checked yet, e.g. right after mount).
  const siteNames = useMemo(() => {
    const ids = selectedSites.length > 0 ? selectedSites : [selectedSite];
    return ids.map(id => sites.find(site => site.id === id)?.name ?? id).filter(Boolean);
  }, [selectedSites, selectedSite, sites]);

  // Baseline snapshot for the page-level Discard/Save changes controls below. Only
  // promoted to the just-submitted days once the save actually succeeds — otherwise a
  // failed save would make Discard "revert" to the same unsaved edits.
  const [savedDays, setSavedDays] = useState<WorkingDay[]>(days);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const hasUnsavedChanges = daysSignature(days) !== daysSignature(savedDays);

  useEffect(() => {
    // Whenever the viewed site or system type changes, `days` is swapped in from the
    // already-fetched configuration for that combo (see workingHoursReducer) - re-baseline
    // Discard/Save against it rather than the previously viewed schedule. Same whenever server
    // configuration lands in `days`, so freshly loaded slots aren't shown as drafts.
    setSavedDays(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, viewingSystemGroupType, configurationLoadId]);

  const handleAddTimeFrame = useCallback(() => {
    const targetDay = days.find(d => d.enabled) ?? days[0];
    if (!targetDay) return;
    if (!targetDay.enabled) onToggleDay(targetDay.id);
    const slotId = `${targetDay.id}-slot-${Date.now()}`;
    onAddSlot({ dayId: targetDay.id, slotId });
    KonnectAnalytics.workingHoursAddSlot({ dayId: targetDay.id, slotId, site: selectedSite });
    onOpenEditor({ dayId: targetDay.id, slotId });
  }, [days, onToggleDay, onAddSlot, selectedSite, onOpenEditor]);

  // Reverts the on-screen (unsaved) days back to the last known-good server state. Used by
  // the explicit Discard button, and also fired automatically when a save request fails —
  // otherwise the failed edit stays visible as if it had gone through, only to silently
  // vanish on the next refresh once the real (unchanged) server state is re-fetched.
  const revertToSavedDays = useCallback(
    (daysToRestore: WorkingDay[]) => {
      daysToRestore.forEach(day => onRestoreDay({ dayId: day.id, slots: day.slots, enabled: day.enabled }));
    },
    [onRestoreDay]
  );

  const handleDiscard = useCallback(() => {
    KonnectAnalytics.productionHoursDiscardClicked({ site: selectedSite });
    revertToSavedDays(savedDays);
  }, [savedDays, revertToSavedDays, selectedSite]);

  const handleSaveChanges = useCallback(
    async (trigger: 'save_button' = 'save_button') => {
      const payload = buildProductionHoursConfigurationPayload({
        siteNames,
        selectedSystemGroupTypes,
        applyMode,
        startDate,
        days,
      });
      KonnectAnalytics.productionHoursSaveChangesClicked({
        siteCount: siteNames.length,
        isMixedSites,
        isMixedSystemGroupTypes,
        trigger,
      });
      setIsSavingAll(true);
      try {
        await saveProductionHoursConfigurationForSites(payload);
        setSavedDays(days);
        KonnectAnalytics.productionHoursSaveChangesResult({ success: true, trigger });
        enqueueNotification('Working hours saved successfully.', { variant: 'success' });
      } catch (error) {
        KonnectAnalytics.productionHoursSaveChangesResult({ success: false, trigger });
        // The POST never actually landed - discard the unconfirmed edit right away rather
        // than leaving it on screen looking saved (savedDays itself is untouched, since
        // nothing changed on the server).
        revertToSavedDays(savedDays);
        enqueueNotification('Failed to save working hours. Please try again.', { variant: 'error' });
      } finally {
        setIsSavingAll(false);
      }
    },
    [
      siteNames,
      selectedSystemGroupTypes,
      applyMode,
      startDate,
      days,
      savedDays,
      revertToSavedDays,
      isMixedSites,
      isMixedSystemGroupTypes,
      saveProductionHoursConfigurationForSites,
      enqueueNotification,
    ]
  );

  useEffect(() => {
    // Sync local working-hours group with the globally selected system group on first mount.
    // Deps intentionally omitted — must run only once to avoid overwriting user's in-page selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!selectedSystemGroup && globalSystemGroup) {
      setSystemGroup(globalSystemGroup);
    }
  }, []);

  useEffect(() => {
    // Re-pick a site whenever the available list changes (e.g. a system-type toggle
    // removed the currently selected site's only group) and the current pick is stale.
    if (sites.some(site => site.id === selectedSite)) return;
    onSiteChange(sites[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  useEffect(() => {
    // Drop any checked "apply to" sites that are no longer in the available list (same
    // trigger as the viewing-site re-pick above).
    const validIds = new Set(sites.map(site => site.id));
    const filtered = selectedSites.filter(id => validIds.has(id));
    if (filtered.length === selectedSites.length) return;
    setSites(filtered.length > 0 ? filtered : sites[0] ? [sites[0].id] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  useEffect(() => {
    // "Apply change to" sites defaults to just the viewing site until the user checks more.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (selectedSites.length === 0 && selectedSite) {
      setSites([selectedSite]);
    }
  }, [selectedSite]);

  useEffect(() => {
    // "Apply to system types" defaults to every type the user has access to, all selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (selectedSystemGroupTypes.length === 0 && userSystemGroup.length > 0) {
      setSystemGroupTypes(userSystemGroup);
    }
  }, [userSystemGroup]);

  useEffect(() => {
    if (!selectedSite || selectedSystemGroupTypes.length === 0 || siteNames.length === 0) return;
    getProductionHoursConfigurationForSites({
      siteNames,
      systemGroupTypes: selectedSystemGroupTypes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteNames, selectedSystemGroupTypes]);

  useEffect(() => {
    // Site-agnostic (no params) — fetch once on mount.
    getProductionHoursMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Pre-fetched for every selected site so the Holidays card's summary (days off count,
    // next upcoming) is available immediately, not only after "Manage holidays" is opened.
    if (!selectedSite) return;
    getProductionHoursHolidays({ siteNames });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteNames]);

  return (
    <Box sx={styles.desktopPage}>
      <Box sx={styles.desktopGrid}>
        <Box sx={styles.desktopMainColumn}>
          <WorkingHoursHeader
            sites={sites}
            selectedSite={selectedSite}
            onSiteChange={onSiteChange}
            selectedSites={selectedSites}
            setSites={setSites}
            applyMode={applyMode}
            onApplyModeChange={onApplyModeChange}
            startDate={startDate}
            onStartDateChange={onStartDateChange}
            userSystemGroup={userSystemGroup}
            selectedSystemGroupTypes={selectedSystemGroupTypes}
            setSystemGroupTypes={setSystemGroupTypes}
            readOnly={readOnly}
          />

          <MixedSitesBanner
            isMixed={isMixedSites}
            selectedSites={selectedSites}
            sites={sites}
            viewingSite={selectedSite}
            onChangeViewing={onSiteChange}
          />

          <MixedScheduleBanner
            isMixed={isMixedSystemGroupTypes}
            selectedSystemGroupTypes={selectedSystemGroupTypes}
            viewingSystemGroupType={viewingSystemGroupType}
            onChangeViewing={setViewingSystemGroupType}
          />

          <Box sx={styles.daysListWrapper}>
            <Box sx={styles.weekHead}>
              <Button
                variant="outlined"
                size="small"
                disabled={readOnly || isLoadingProductionHoursConfiguration}
                onClick={handleAddTimeFrame}
                sx={styles.newTimeFrameButton}>
                + Add time frame
              </Button>
              <Box sx={styles.weekHint}>
                Adds a time frame to the first working day, or double-click a day's timeline to
                add one directly.
              </Box>
            </Box>

            <Box sx={styles.daysList}>
              {isLoadingProductionHoursConfiguration
                ? days.map((day, index) => (
                    <React.Fragment key={day.id}>
                      <DayRowSkeleton />
                      {index !== days.length - 1 && <Divider sx={styles.dayDivider} />}
                    </React.Fragment>
                  ))
                : days.map((day, index) => (
                    <React.Fragment key={day.id}>
                      <DayRow
                        day={day}
                        days={days}
                        savedDay={savedDays.find(saved => saved.id === day.id)}
                        editor={editor}
                        editorValidation={editorValidation}
                        onToggleDay={onToggleDay}
                        onAddSlot={onAddSlot}
                        onAddSlotAt={onAddSlotAt}
                        onUpdateSlot={onUpdateSlot}
                        onRemoveSlot={onRemoveSlot}
                        onRestoreDay={onRestoreDay}
                        onOpenEditor={onOpenEditor}
                        onCloseEditor={onCloseEditor}
                        onUpdateEditorDraft={onUpdateEditorDraft}
                        onSetEditorApplyDays={onSetEditorApplyDays}
                        onApplyEditor={onApplyEditor}
                        onSaveChanges={handleSaveChanges}
                        selectedSite={selectedSite}
                        readOnly={readOnly}
                      />
                      {index !== days.length - 1 && <Divider sx={styles.dayDivider} />}
                    </React.Fragment>
                  ))}
            </Box>

            <Box sx={styles.weekFoot}>
              <Box sx={styles.footerHint}>
                15-minute resolution · frames snap to :00 / :15 / :30 / :45
              </Box>
              <Box sx={styles.footerActions}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={readOnly || !hasUnsavedChanges || isSavingAll || isLoadingProductionHoursConfiguration}
                  onClick={handleDiscard}
                  sx={styles.cancelButton}>
                  Discard
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={readOnly || !hasUnsavedChanges || isSavingAll || isLoadingProductionHoursConfiguration}
                  onClick={() => handleSaveChanges('save_button')}
                  sx={styles.saveButton}>
                  Save changes
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.sideColumn}>
          <HolidaysCardContainer siteNames={siteNames} />
          <ProductionHoursMatrix
            entries={productionHoursMatrix?.entries ?? []}
            currentSiteName={currentSiteName}
            isLoading={isLoadingProductionHoursMatrix}
          />
        </Box>
      </Box>

      <HolidaysModalContainer />
    </Box>
  );
};

export default WorkingHoursDesktop;
